import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LayoutContainer from 'components/Common/LayoutContainer'
import ChatLandingFloatingImages from './ChatLandingFloatingImages'
import ChatLandingHero from './ChatLandingHero'
import ChatLandingSuggestedActionTile from './ChatLandingSuggestedActionTile'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useChatList } from 'tripPlanner/hooks/api/chat'
import Group from 'components/utils/Group'
import ChatLandingRecentTripTile from './ChatLandingRecentTripTile'
import ChatLandingUpcomingOrderTile from './ChatLandingUpcomingOrderTile'
import ChatLandingFlightOrderTile from './ChatLandingFlightOrderTile'
import ChatLandingRecentSearchTile from './ChatLandingRecentSearchTile'
import ChatLandingViewedOfferTile from './ChatLandingViewedOfferTile'
import useOrders from 'hooks/Order/useOrders'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { getHighIntentHotelOffers } from 'selectors/recomendations/highIntentSelectors'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import { isOrderEligibleForChatTrip } from 'tripPlanner/hooks/useOrderSupportsChatTripGeneration'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { isItemCancelled, sortOrdersByDeparture } from 'lib/order/orderUtils'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { deriveFlightDisplayData, formatFlightRouteLabel, formatFlightTypeLabel } from './flightDisplayUtils'

const LandingContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ContentWrapper = styled(LayoutContainer)`
  position: relative;
  z-index: 1;
  padding: ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(24)};
`

type CardFilter = 'bookings' | 'recentSearches' | 'viewedOffers' | 'suggestions'
const MAX_BOOKING_TILES = 12

interface CardFilterOption {
  id: CardFilter
  title: string
}

type CarouselTile =
  | { type: 'booking'; data: OrderTileData }
  | { type: 'recentSearch'; data: { id: string; locationName: string; checkInDate?: string; checkOutDate?: string; totalAdults?: number } }
  | { type: 'viewedOffer'; offerId: string }
  | { type: 'suggestedAction'; label: string }

function getViewedOfferTags(propertyName: string, location?: string): Array<string> {
  const parts: Array<string> = [propertyName]
  if (location) {
    parts.push(location)
  }
  return parts
}

function shuffleArray<T>(array: Array<T>): Array<T> {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }
  return shuffled
}

interface HotelOrderTileData {
  bookingType: 'hotel'
  itemId: string
  orderId: string
  offerId: string
  propertyName: string
  checkInDate?: string
  imageId?: string
  checkOutDate?: string
  occupancy?: App.Occupants
}

interface FlightOrderTileData {
  bookingType: 'flight'
  itemId: string
  orderId: string
  offerId: string
  reservationId?: string
}

type OrderTileData = HotelOrderTileData | FlightOrderTileData

function getFlightBookingTag(
  intl: IntlShape,
  flightDetails?: App.FlightOrderItemDetails,
): string {
  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const routeText = formatFlightRouteLabel(intl, departureLabel, arrivalLabel)
  const flightTypeText = formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight)

  let dateText = ''
  if (departureDate) {
    if (isReturnFlight && arrivalDate) {
      dateText = getCheckInCheckOutText(departureDate, arrivalDate)
    } else {
      dateText = moment(departureDate).format(DMY_CASUAL_SHORT_FORMAT)
    }
  }

  const flightTagParts = [routeText, flightTypeText]
  if (dateText) {
    flightTagParts.push(dateText)
  }

  return flightTagParts.join(' · ')
}

function getOrderTileDataList(order: App.Order): Array<OrderTileData> {
  const tiles: Array<OrderTileData> = []

  for (const item of order.items) {
    if (isItemCancelled(item) || !item.reservation?.startDate || !item.offer?.property?.name) continue
    tiles.push({
      bookingType: 'hotel',
      itemId: item.id,
      orderId: order.id,
      offerId: item.offerId,
      propertyName: item.offer.property.name,
      checkInDate: item.reservation.startDate,
      imageId: item.offer.property?.roomType?.images?.[0]?.id,
      checkOutDate: item.reservation.endDate,
      occupancy: { adults: item.reservation.adults, children: item.reservation.children, infants: item.reservation.infants },
    })
  }

  for (const item of order.bedbankItems) {
    if (isItemCancelled(item) || !item.offer?.property?.name) continue
    tiles.push({
      bookingType: 'hotel',
      itemId: item.id,
      orderId: order.id,
      offerId: item.offer.id,
      propertyName: item.offer.property.name,
      checkInDate: item.checkIn.format(ISO_DATE_FORMAT),
      imageId: item.offer.image?.id,
      checkOutDate: item.checkOut.format(ISO_DATE_FORMAT),
      occupancy: item.rooms[0]?.occupancy,
    })
  }

  if (order.hasFlight) {
    for (const item of order.flightItems) {
      if (isItemCancelled(item)) continue
      tiles.push({
        bookingType: 'flight',
        itemId: item.id,
        orderId: order.id,
        offerId: item.reservationId ?? item.id,
        reservationId: item.reservationId,
      })
    }
  }
  return tiles
}

export interface BookingSelection {
  orderIds: Array<string>
  offerIds: Array<string>
}

interface Props {
  onSubmit: (message: string) => void
  onAddTags?: (tags: Array<string>) => void
  disabled?: boolean
  files?: Array<File>
  onFilesChange?: (files: Array<File>) => void
  selectedTags?: Array<string>
  onRemoveTag?: (tag: string) => void
  onBookingSelectionChange?: (selection: BookingSelection) => void
  className?: string
}

function ChatLandingPage({
  onSubmit,
  onAddTags,
  disabled = false,
  files,
  onFilesChange,
  selectedTags,
  onRemoveTag,
  onBookingSelectionChange,
  className,
}: Props) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const {
    data,
    isLoading,
    isError,
  } = useChatList({ enabled: isLoggedIn, staleTime: 30_000 })

  const intl = useIntl()
  const [upcomingOrders, ordersFetched] = useOrders('upcoming')
  const flightDetailsState = useAppSelector(state => state.orders.flightDetails)
  const recentSearches = useRecentSearches()
  const previouslyViewedOffers = useAppSelector(getHighIntentHotelOffers)

  const viewedOfferIds = useMemo(() => {
    return previouslyViewedOffers.slice(0, 3).map(offer => offer.offerId)
  }, [previouslyViewedOffers])

  const bookingTiles = useMemo(() => {
    if (!ordersFetched) return []

    const eligible = upcomingOrders.filter(isOrderEligibleForChatTrip)
    const sorted = sortOrdersByDeparture(eligible)
    return sorted.flatMap(getOrderTileDataList).slice(0, MAX_BOOKING_TILES)
  }, [ordersFetched, upcomingOrders])

  const recentSearchTiles = useMemo(() => {
    return recentSearches.slice(0, 3).map(search => {
      const totalAdults = search.rooms.reduce((sum, room) => sum + (room.adults ?? 0), 0)
      return {
        id: search.recentSearchId,
        locationName: search.searchItem.format.mainText,
        checkInDate: search.dates.checkIn,
        checkOutDate: search.dates.checkOut,
        totalAdults: totalAdults || undefined,
      }
    })
  }, [recentSearches])

  const suggestedActions = useMemo(() => {
    if (bookingTiles.length > 0) {
      return [
        intl.formatMessage({ id: '4Z28PhruhsoRynk_1pDQ', defaultMessage: 'What should I add to this stay?', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: 'yUTa_ndVqkTREpBIInVR', defaultMessage: 'Help me make the most of these dates', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: 'wRqFE56_R0Z4vEQZJazx', defaultMessage: 'Add activities and restaurants near my hotel', description: 'Quick action pill when an order is present on the chat landing page' }),
        intl.formatMessage({ id: '862OnDklLTJYvRCINrXh', defaultMessage: 'Create a day-by-day plan for this trip', description: 'Quick action pill when an order is present on the chat landing page' }),
      ]
    }
    return [
      intl.formatMessage({ id: 'bOWGp05rGbRD2ETaAw6b', defaultMessage: 'Take a tour of TripPlanner', description: 'Quick action button label to start a guided tour of the TripPlanner feature' }),
      intl.formatMessage({ id: 'BjPZOZzTziLpw1ywsGGo', defaultMessage: 'Where should I go?', description: 'Quick action button label to get destination suggestions from TripPlanner AI' }),
      intl.formatMessage({ id: 'oWIAGH5r6qOgN3HXJrXh', defaultMessage: 'Local trip ideas', description: 'Quick action button label to get local trip ideas from the TripPlanner AI' }),
      intl.formatMessage({ id: 'm3p5axiC0ZIbPox_-doP', defaultMessage: 'Weekend escapes', description: 'Quick action button label to get weekend escape ideas from TripPlanner AI' }),
    ]
  }, [bookingTiles.length, intl])

  const [selectedFilter, setSelectedFilter] = useState<CardFilter | undefined>()

  const filterOptions = useMemo<Array<CardFilterOption>>(() => {
    const options: Array<CardFilterOption> = []

    if (isLoggedIn && bookingTiles.length > 0) {
      options.push({
        id: 'bookings',
        title: intl.formatMessage({ id: 'BW263IdRuVxs-vDKap2d', defaultMessage: 'Your bookings', description: 'Filter chip label for bookings on the chat landing page' }),
      })
    }
    if (recentSearchTiles.length > 0) {
      options.push({
        id: 'recentSearches',
        title: intl.formatMessage({ id: 'Ri9ED07d8dIKeE2InE_G', defaultMessage: 'Recent searches', description: 'Filter chip label for recent searches on the chat landing page' }),
      })
    }
    if (viewedOfferIds.length > 0) {
      options.push({
        id: 'viewedOffers',
        title: intl.formatMessage({ id: 'nDHRM5wRj7Wu8i7KRD_P', defaultMessage: 'Previously viewed offers', description: 'Filter chip label for previously viewed offers on the chat landing page' }),
      })
    }
    options.push({
      id: 'suggestions',
      title: intl.formatMessage({ id: 'ZkUz3z0NpTKmYQ7P5CFC', defaultMessage: 'Suggestions', description: 'Filter chip label for suggested actions on the chat landing page' }),
    })

    return options
  }, [isLoggedIn, bookingTiles.length, recentSearchTiles.length, viewedOfferIds.length, intl])

  useEffect(() => {
    if (selectedFilter && !filterOptions.some(opt => opt.id === selectedFilter)) {
      setSelectedFilter(undefined)
    }
  }, [filterOptions, selectedFilter])

  const handleFilterClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const filterId = e.currentTarget.dataset.filter as CardFilter
    setSelectedFilter(prev => prev === filterId ? undefined : filterId)
  }, [])

  const tileDataKey = useMemo(() => {
    const bookingIds = bookingTiles.map(t => t.itemId).join(',')
    const searchIds = recentSearchTiles.map(t => t.id).join(',')
    const offerIds = viewedOfferIds.join(',')
    return `${bookingIds}|${searchIds}|${offerIds}|${suggestedActions.length}`
  }, [bookingTiles, recentSearchTiles, viewedOfferIds, suggestedActions])

  const shuffledTilesRef = useRef<{ key: string; tiles: Array<CarouselTile> }>({ key: '', tiles: [] })
  if (shuffledTilesRef.current.key !== tileDataKey) {
    const bookings: Array<CarouselTile> = bookingTiles.map(data => ({ type: 'booking' as const, data }))
    const rest: Array<CarouselTile> = [
      ...recentSearchTiles.map(data => ({ type: 'recentSearch' as const, data })),
      ...viewedOfferIds.map(offerId => ({ type: 'viewedOffer' as const, offerId })),
      ...suggestedActions.map(label => ({ type: 'suggestedAction' as const, label })),
    ]
    shuffledTilesRef.current = { key: tileDataKey, tiles: [...bookings, ...shuffleArray(rest)] }
  }
  const shuffledAllTiles = shuffledTilesRef.current.tiles

  const { bookingTagsByItemId, bookingDataByTag } = useMemo(() => {
    const tagsByItemId = new Map<string, string>()
    const dataByTag = new Map<string, OrderTileData>()
    for (const tile of bookingTiles) {
      const parts: Array<string> = []
      if (tile.bookingType === 'flight') {
        parts.push(getFlightBookingTag(intl, flightDetailsState[tile.itemId]?.flight))
      } else {
        parts.push(tile.propertyName)
        if (tile.checkInDate && tile.checkOutDate) {
          parts.push(getCheckInCheckOutText(tile.checkInDate, tile.checkOutDate))
        }
        if (tile.occupancy?.adults) {
          parts.push(intl.formatMessage(
            {
              id: 'hd1_9T_9cOUVixrwCqmW',
              defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
              description: 'Pluralised adult count label for upcoming booking tile on chat page',
            },
            { count: tile.occupancy.adults },
          ))
        }
      }
      const tag = parts.join(' · ')
      tagsByItemId.set(tile.itemId, tag)
      dataByTag.set(tag, tile)
    }
    return { bookingTagsByItemId: tagsByItemId, bookingDataByTag: dataByTag }
  }, [bookingTiles, flightDetailsState, intl])

  const prevBookingKeyRef = useRef('')

  useEffect(() => {
    const orderIdSet = new Set<string>()
    const offerIdSet = new Set<string>()
    for (const tag of selectedTags ?? []) {
      const tile = bookingDataByTag.get(tag)
      if (tile) {
        orderIdSet.add(tile.orderId)
        offerIdSet.add(tile.offerId)
      }
    }
    const orderIds = [...orderIdSet].sort()
    const offerIds = [...offerIdSet].sort()
    const key = `${orderIds.join(',')}|${offerIds.join(',')}`
    if (key !== prevBookingKeyRef.current) {
      prevBookingKeyRef.current = key
      onBookingSelectionChange?.({ orderIds, offerIds })
    }
  }, [selectedTags, onBookingSelectionChange, bookingDataByTag])

  const getRecentSearchTags = useCallback((locationName: string, checkInDate?: string, checkOutDate?: string, totalAdults?: number) => {
    const parts: Array<string> = [locationName]
    if (checkInDate && checkOutDate) {
      parts.push(getCheckInCheckOutText(checkInDate, checkOutDate))
    }
    if (totalAdults) {
      parts.push(intl.formatMessage(
        {
          id: '8Wb0texqenDfYTvPGAQI',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Pluralised adult count label for recent search tile on chat page',
        },
        { count: totalAdults },
      ))
    }
    return parts
  }, [intl])

  const selectedTagSet = useMemo(() => new Set(selectedTags ?? []), [selectedTags])

  const toggleTag = useCallback((tag: string) => {
    if (selectedTagSet.has(tag)) {
      onRemoveTag?.(tag)
    } else {
      onAddTags?.([tag])
    }
  }, [selectedTagSet, onRemoveTag, onAddTags])

  const handleBookingTileClick = useCallback((itemId: string) => {
    const tag = bookingTagsByItemId.get(itemId)
    if (!tag) return
    toggleTag(tag)
  }, [bookingTagsByItemId, toggleTag])

  const handleSuggestedActionClick = useCallback((label: string) => {
    toggleTag(label)
  }, [toggleTag])

  const handleRecentSearchClick = useCallback((locationName: string, checkInDate?: string, checkOutDate?: string, totalAdults?: number) => {
    const tags = getRecentSearchTags(locationName, checkInDate, checkOutDate, totalAdults)
    const allSelected = tags.every(tag => selectedTagSet.has(tag))
    if (allSelected) {
      tags.forEach(tag => onRemoveTag?.(tag))
    } else {
      onAddTags?.(tags)
    }
  }, [getRecentSearchTags, selectedTagSet, onRemoveTag, onAddTags])

  const handleViewedOfferClick = useCallback((propertyName: string, location?: string) => {
    const tags = getViewedOfferTags(propertyName, location)
    const allSelected = tags.every(tag => selectedTagSet.has(tag))
    if (allSelected) {
      tags.forEach(tag => onRemoveTag?.(tag))
    } else {
      onAddTags?.(tags)
    }
  }, [selectedTagSet, onRemoveTag, onAddTags])

  const visibleTiles = useMemo<Array<CarouselTile>>(() => {
    switch (selectedFilter) {
      case undefined:
        return shuffledAllTiles
      case 'bookings':
        return bookingTiles.map(data => ({ type: 'booking' as const, data }))
      case 'recentSearches':
        return recentSearchTiles.map(data => ({ type: 'recentSearch' as const, data }))
      case 'viewedOffers':
        return viewedOfferIds.map(offerId => ({ type: 'viewedOffer' as const, offerId }))
      case 'suggestions':
        return suggestedActions.map(label => ({ type: 'suggestedAction' as const, label }))
    }
  }, [selectedFilter, shuffledAllTiles, bookingTiles, recentSearchTiles, viewedOfferIds, suggestedActions])

  const showRecentTrips = isLoggedIn && !isError && data && data.length > 0 && !isLoading

  return (
    <LandingContainer className={className}>
      <ChatLandingFloatingImages />
      <ContentWrapper variant="medium">
        <Group direction="vertical" gap={40} fullWidth>
          <Group direction="vertical" gap={20} fullWidth>
            <ChatLandingHero
              onSubmit={onSubmit}
              disabled={disabled}
              files={files}
              onFilesChange={onFilesChange}
              selectedTags={selectedTags}
              onRemoveTag={onRemoveTag}
            />
          </Group>
          {filterOptions.length > 0 && (
            <Group direction="vertical" gap={12} fullWidth>
              <BodyText variant="medium" weight="bold" align="start">
                <FormattedMessage
                  id="v5ph4Ew9wkBgWmb7V0Zo"
                  defaultMessage="Ideas to get started"
                  description="Heading for the card filter section on the chat landing page"
                />
              </BodyText>
              <HorizontalContainerWithFadedOverflow>
                <Group direction="horizontal" gap={8}>
                  {filterOptions.map(option => (
                    <FilterChip
                      key={option.id}
                      data-filter={option.id}
                      selected={option.id === selectedFilter}
                      onClick={handleFilterClick}
                      size="medium"
                    >
                      {option.title}
                    </FilterChip>
                  ))}
                </Group>
              </HorizontalContainerWithFadedOverflow>
              <HorizontalContainerWithFadedOverflow gap={12} overflowGutter={0}>
                {visibleTiles.map(tile => {
                  switch (tile.type) {
                    case 'booking':
                      if (tile.data.bookingType === 'flight') {
                        return (
                          <ChatLandingFlightOrderTile
                            key={tile.data.itemId}
                            itemId={tile.data.itemId}
                            orderId={tile.data.orderId}
                            selected={selectedTagSet.has(bookingTagsByItemId.get(tile.data.itemId) ?? '')}
                            onClick={handleBookingTileClick}
                          />
                        )
                      }
                      return (
                        <ChatLandingUpcomingOrderTile
                          key={tile.data.itemId}
                          itemId={tile.data.itemId}
                          propertyName={tile.data.propertyName}
                          imageId={tile.data.imageId}
                          checkInDate={tile.data.checkInDate}
                          checkOutDate={tile.data.checkOutDate}
                          occupancy={tile.data.occupancy}
                          selected={selectedTagSet.has(bookingTagsByItemId.get(tile.data.itemId) ?? '')}
                          onClick={handleBookingTileClick}
                        />
                      )
                    case 'recentSearch':
                      return (
                        <ChatLandingRecentSearchTile
                          key={tile.data.id}
                          locationName={tile.data.locationName}
                          checkInDate={tile.data.checkInDate}
                          checkOutDate={tile.data.checkOutDate}
                          totalAdults={tile.data.totalAdults}
                          selected={getRecentSearchTags(tile.data.locationName, tile.data.checkInDate, tile.data.checkOutDate, tile.data.totalAdults).every(tag => selectedTagSet.has(tag))}
                          onClick={handleRecentSearchClick}
                        />
                      )
                    case 'viewedOffer':
                      return (
                        <ChatLandingViewedOfferTile
                          key={tile.offerId}
                          offerId={tile.offerId}
                          selectedTagSet={selectedTagSet}
                          onClick={handleViewedOfferClick}
                        />
                      )
                    case 'suggestedAction':
                      return (
                        <ChatLandingSuggestedActionTile
                          key={tile.label}
                          label={tile.label}
                          selected={selectedTagSet.has(tile.label)}
                          onClick={handleSuggestedActionClick}
                        />
                      )
                    default:
                      return null
                  }
                })}
              </HorizontalContainerWithFadedOverflow>
            </Group>
          )}
          {showRecentTrips && (
            <Group direction="vertical" gap={12} fullWidth>
              <BodyText variant="medium" weight="bold" align="start">
                <FormattedMessage
                  id="UEQw2vs7h-yijG4jHbDN"
                  defaultMessage="Recent Trips"
                  description="Heading for recent trips section in the trip planner feature"
                />
              </BodyText>
              <Group direction="horizontal" gap={12} wrap="wrap" fullWidth>
                {data?.slice(0, 3).map(chat => (
                  <ChatLandingRecentTripTile
                    key={chat.id}
                    title={chat.name}
                    updatedAt={chat.updatedAt}
                    to={`/trip-planner/chat-trip-planner/${chat.id}`}
                  />
                ))}
              </Group>
            </Group>
          )}
        </Group>
      </ContentWrapper>
    </LandingContainer>
  )
}

export default ChatLandingPage
